// src/features/system/developer/components/video-gallery/VideoCard.tsx
// ────────────────────────────────────────────────────────────────────────────
// Single video tile — PAC Glass design, dark-only.
//
// UX choices:
//   • Hover/focus reveals the play overlay, lifts border (no bg swap).
//   • Click opens an inline preview modal (signed playback url, on-demand).
//   • Status badge mirrors the orchestrator lifecycle.
//   • Skeleton-first: poster has a neutral placeholder until preview loads.
//   • Keyboard accessible (focusable, Enter/Space triggers play).
// ────────────────────────────────────────────────────────────────────────────
import React, { memo, useCallback, useEffect, useRef, useState } from 'react';
import type { PosterUrlResponse, PreviewUrlResponse, VideoFeedItem } from './types';
import { formatBytes, formatDuration, statusTone, warmingTone } from './format';
import { pipelineProgress, WARMING_UP_WINDOW_MS } from './states';
import LiveTimeAgo from './LiveTimeAgo';
import { MapPinLink } from './MapPinLink';
import ShareLinkButton from './ShareLinkButton';
import SidecarsBadge from './SidecarsBadge';

interface Props {
  item: VideoFeedItem;
  onOpen: (item: VideoFeedItem) => void;
  onDelete?: (item: VideoFeedItem) => void;
  deleting?: boolean;
  /**
   * Optional lazy poster URL resolver. When provided, the card fetches
   * a signed READ url for the per-video JPEG sidecar once it enters the
   * viewport (IntersectionObserver-gated) and renders it as an `<img>`
   * over the placeholder with an opacity cross-fade. Returns `null`
   * when the sidecar is not yet available — in that case the card
   * keeps the static placeholder visible. Single-flight + cached at
   * the hook layer (`useVideoGallery.requestPoster`).
   */
  requestPoster?: (videoId: string) => Promise<PosterUrlResponse | null>;
  /**
   * Optional lazy preview-clip URL resolver. When provided AND the
   * card is hovered (pointer or keyboard focus), the card fetches a
   * signed READ url for the per-video MP4 preview sidecar (3 s,
   * muted, 480p, +faststart) and renders it as an autoplaying
   * `<video>` overlay above the poster with an opacity cross-fade.
   * Pauses + resets on mouseleave/blur so re-hover replays from t=0.
   *
   * Hover-gated (NOT viewport-gated) by design: preview MP4s are
   * roughly 3–5× the size of poster JPEGs (˜150–300 KB vs ˜50–70 KB),
   * so we only pay the bytes when the user signals intent. Mirrors
   * the TikTok / YouTube Shorts / Instagram Reels approach where the
   * thumbnail loads on scroll but the preview clip is fetched on
   * tap/hover. Returns `null` when the sidecar is not yet ready —
   * the card silently keeps the poster visible.
   */
  requestPreview?: (videoId: string) => Promise<PreviewUrlResponse | null>;
  /**
   * IG-style dense grid mode. When true the card collapses to a flat
   * zero-chrome square tile (aspect-square, no rounding, no border, no
   * floating chips). Matches Instagram / TikTok profile grids. Hover/
   * focus still reveals the play glyph and the muted preview clip so
   * we keep the high-end interaction model on top of a minimalist look.
   */
  dense?: boolean;
}

function VideoCardImpl({ item, onOpen, onDelete, deleting = false, requestPoster, requestPreview, dense = false }: Props) {
  // Post-dispatch cold-start window: after the worker-agile scale-to-zero
  // migration, a freshly `uploaded` row may wait a few seconds while Pub/Sub
  // PUSH wakes the cold worker. We surface that as "Warming up the processor…"
  // (amber, pulsing) instead of a static "Queued", derived purely from the
  // row's age — no extra round trip. Falls back to the normal tone past the
  // window so a genuinely stuck row never reads "warming" forever.
  const ageMs = item.created_at_ms > 0 ? Date.now() - item.created_at_ms : -1;
  const progress = pipelineProgress(item.status, ageMs);
  const tone = progress.isWarming ? warmingTone() : statusTone(item.status);

  // Self-tick the warming window precisely. The gallery only refetches every
  // ~15 s, but the cold-start window is ~20 s — without this the badge would
  // flip "Warming up…" → "Queued" raggedly (whenever the next poll lands)
  // rather than on the boundary. We schedule ONE setTimeout that fires exactly
  // when the window expires, forcing a single re-render that drops `isWarming`.
  // O(1) per warming card, auto-cleared on status change / unmount — no 1 Hz
  // interval, no background CPU once the window has elapsed.
  const [, bumpWarmTick] = useState(0);
  useEffect(() => {
    if (item.status !== 'uploaded' || item.created_at_ms <= 0) return;
    const remaining = item.created_at_ms + WARMING_UP_WINDOW_MS - Date.now();
    if (remaining <= 0) return; // already past the window — nothing to schedule
    const t = setTimeout(() => bumpWarmTick((n) => n + 1), remaining);
    return () => clearTimeout(t);
  }, [item.status, item.created_at_ms]);

  const [hovered, setHovered] = useState(false);
  // ── Poster lazy-load (V3) ────────────────────────────────────────
  // Fetch the signed poster URL only after the card enters the viewport
  // (IntersectionObserver, 50% root margin so it lights up just before
  // it appears on scroll) so a 200-card grid does not issue 200 BFF
  // round trips on first paint. The hook layer single-flights + caches
  // per `video_id`, so re-entries (scroll-back) reuse the in-flight or
  // cached URL. Failure / `null` keeps the static placeholder visible.
  const [posterUrl, setPosterUrl] = useState<string | null>(null);
  const [posterLoaded, setPosterLoaded] = useState(false);
  // First-frame fallback flag — when the poster sidecar is not yet
  // available, we transparently use the preview-clip MP4 as a poster
  // surrogate (first frame only, paused, muted). This is what TikTok,
  // YouTube Shorts, and IG Reels do when their static thumbnail is in
  // flight. Gated by viewport so we don't pay MP4 metadata bytes for
  // off-screen cards.
  const [inViewport, setInViewport] = useState(false);
  const articleRef = useRef<HTMLElement | null>(null);
  useEffect(() => {
    if (!requestPoster) return;
    const el = articleRef.current;
    if (!el || typeof IntersectionObserver === 'undefined') {
      // No viewport gating possible — fetch immediately (degrade gracefully).
      setInViewport(true);
      let cancelled = false;
      requestPoster(item.video_id).then((r) => {
        if (!cancelled && r?.poster_url) setPosterUrl(r.poster_url);
      }).catch(() => { /* swallow — placeholder stays */ });
      return () => { cancelled = true; };
    }
    let cancelled = false;
    let posterRequested = false;
    const io = new IntersectionObserver((entries) => {
      for (const e of entries) {
        if (e.isIntersecting) {
          setInViewport(true);
          if (!posterRequested) {
            posterRequested = true;
            requestPoster(item.video_id).then((r) => {
              if (!cancelled && r?.poster_url) setPosterUrl(r.poster_url);
            }).catch(() => { /* placeholder stays */ });
          }
        } else {
          setInViewport(false);
        }
      }
    }, { rootMargin: '200px' });
    io.observe(el);
    return () => {
      cancelled = true;
      io.disconnect();
    };
  }, [requestPoster, item.video_id]);

  // ── Sidecar self-heal event subscription ─────────────────────────
  // When the hook's background retry loop resolves a previously
  // 202-pending sidecar, it dispatches a window CustomEvent. We
  // subscribe filtered by `video_id` so already-mounted cards rebind
  // without forcing react-query subscription plumbing into every tile.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const onPoster = (e: Event) => {
      const detail = (e as CustomEvent).detail || {};
      if (detail.videoId === item.video_id && typeof detail.posterUrl === 'string') {
        setPosterUrl(detail.posterUrl);
      }
    };
    const onPreview = (e: Event) => {
      const detail = (e as CustomEvent).detail || {};
      if (detail.videoId === item.video_id && typeof detail.previewUrl === 'string') {
        setPreviewUrl(detail.previewUrl);
      }
    };
    window.addEventListener('pac:poster:resolved', onPoster as EventListener);
    window.addEventListener('pac:preview:resolved', onPreview as EventListener);
    return () => {
      window.removeEventListener('pac:poster:resolved', onPoster as EventListener);
      window.removeEventListener('pac:preview:resolved', onPreview as EventListener);
    };
  }, [item.video_id]);

  // ── Hover-to-preview (V3) ────────────────────────────────────────
  // Strictly hover/focus-gated (NOT IntersectionObserver) because the
  // 3 s preview MP4 is 3–5× the byte cost of the poster JPEG, and a
  // 200-card grid scrolled past must never trigger 200 MP4 fetches.
  // Matches the TikTok / IG Reels / YT Shorts pattern: thumbnail on
  // scroll, preview clip only on actual pointer/keyboard intent.
  //
  // Debounce by 120 ms so a hover-fly-over (cursor sweeping across
  // the grid) doesn't issue a request for every card it crosses.
  // 120 ms matches TikTok's empirical hover dwell threshold and is
  // well below the visual ~200 ms perception threshold.
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [previewPlaying, setPreviewPlaying] = useState(false);
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const hoverTimerRef = useRef<number | null>(null);
  const previewRequestedRef = useRef(false);

  // ── First-frame fallback (P3) ────────────────────────────────────
  // When the poster sidecar is missing AND the card is in viewport,
  // eagerly request the preview clip — its first frame doubles as a
  // poster surrogate. Mirrors the TikTok / YT Shorts / IG Reels visual
  // contract: the user always sees the actual video content frame,
  // never a black box. Strictly viewport-gated so a feed scrolled past
  // hundreds of cards never pays the preview MP4 metadata cost for
  // off-screen tiles. The preview is naturally cached (immutable
  // sidecar) so this also warms the hover-to-play interaction.
  useEffect(() => {
    if (!requestPreview) return;
    if (posterUrl) return;            // poster present → no need
    if (!inViewport) return;          // off-screen → don't burn bytes
    if (previewRequestedRef.current) return;
    previewRequestedRef.current = true;
    let cancelled = false;
    requestPreview(item.video_id)
      .then((r) => {
        if (!cancelled && r?.preview_url) setPreviewUrl(r.preview_url);
        // null → preview not ready either; skeleton + placeholder
        // remain. The hook will retry in the background.
      })
      .catch(() => { previewRequestedRef.current = false; });
    return () => { cancelled = true; };
  }, [requestPreview, item.video_id, posterUrl, inViewport]);

  const cancelHoverTimer = useCallback(() => {
    if (hoverTimerRef.current != null) {
      window.clearTimeout(hoverTimerRef.current);
      hoverTimerRef.current = null;
    }
  }, []);

  const triggerPreview = useCallback(() => {
    if (!requestPreview || previewRequestedRef.current) return;
    cancelHoverTimer();
    hoverTimerRef.current = window.setTimeout(() => {
      hoverTimerRef.current = null;
      previewRequestedRef.current = true;
      requestPreview(item.video_id)
        .then((r) => {
          if (r?.preview_url) setPreviewUrl(r.preview_url);
          // null → sidecar not ready; keep poster visible. The hook
          // layer already negative-cached this for 60 s, so re-hovers
          // won't re-fire the request. Allow a later retry by leaving
          // previewRequestedRef true (the hook layer is the gate).
        })
        .catch(() => {
          // Silent: poster stays. Hover preview is enhancement-only.
          previewRequestedRef.current = false;
        });
    }, 120);
  }, [requestPreview, item.video_id, cancelHoverTimer]);

  const stopPreview = useCallback(() => {
    cancelHoverTimer();
    const v = videoRef.current;
    if (v) {
      try { v.pause(); v.currentTime = 0; } catch { /* ignore */ }
    }
    setPreviewPlaying(false);
  }, [cancelHoverTimer]);

  // Cleanup pending hover timer on unmount.
  useEffect(() => () => cancelHoverTimer(), [cancelHoverTimer]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onOpen(item);
    }
  };

  const handleDelete = (e: React.MouseEvent | React.KeyboardEvent) => {
    e.stopPropagation();
    if (!onDelete || deleting) return;
    onDelete(item);
  };

  return (
    <article
      ref={articleRef}
      role="button"
      tabIndex={0}
      onClick={() => onOpen(item)}
      onKeyDown={handleKeyDown}
      onMouseEnter={() => { setHovered(true); triggerPreview(); }}
      onMouseLeave={() => { setHovered(false); stopPreview(); }}
      onFocus={() => { setHovered(true); triggerPreview(); }}
      onBlur={() => { setHovered(false); stopPreview(); }}
      className={[
        'group relative flex flex-col overflow-hidden cursor-pointer',
        dense
          ? 'rounded-none border-0 bg-black'
          : 'rounded-xl pac-glass border border-neutral-700/40 hover:border-neon_purple-400/60 focus-visible:border-neon_orange-300',
        'transition-colors duration-150',
        'focus:outline-none',
        dense ? 'focus-visible:ring-1 focus-visible:ring-white/40 focus-visible:ring-inset'
              : 'focus-visible:ring-2 focus-visible:ring-neon_orange-300/40',
        // Perf: skip layout+paint for offscreen cards. The browser keeps the
        // intrinsic size reserved (so scrollbar height is correct) but defers
        // rendering until the card enters the viewport. Huge win for 200-card
        // grids. Safe fallback to normal rendering on browsers without support.
        'pac-card-cv',
        deleting ? 'opacity-50 pointer-events-none' : '',
      ].join(' ')}
      aria-label={`Play ${item.display_name ?? `video ${item.scope_key}`}`}
      aria-busy={deleting || undefined}
    >
      {/* Poster / placeholder ─────────────────────────────────────── */}
      <div className={[
        'relative w-full overflow-hidden bg-neutral-950',
        dense ? 'aspect-square' : 'aspect-video',
      ].join(' ')}>
        {/* Subtle gradient + container badge */}
        <div className="absolute inset-0 bg-gradient-to-br from-neon_purple-900/30 via-neutral-900 to-neutral-950" />        {/* Poster image — cross-fades in over the placeholder when the
            signed URL resolves AND the image bytes successfully decode.
            On error (404 after sign, expired URL race, etc.) we revert
            to the placeholder by clearing `posterLoaded`. Image is
            decorative; alt is empty so AT does not announce it (the
            article's aria-label already names the video). */}
        {posterUrl && (
          <img
            src={posterUrl}
            alt=""
            aria-hidden
            loading="lazy"
            decoding="async"
            draggable={false}
            onLoad={() => setPosterLoaded(true)}
            onError={() => { setPosterUrl(null); setPosterLoaded(false); }}
            className={[
              'absolute inset-0 w-full h-full object-cover',
              'transition-opacity duration-200 ease-out',
              posterLoaded ? 'opacity-100' : 'opacity-0',
            ].join(' ')}
          />
        )}
        {/* Preview-clip overlay — dual purpose:
              • Hover/focus mode: cross-fades in over the poster once
                the signed MP4 URL resolves AND playback actually starts.
              • Fallback mode (poster missing): renders the first frame
                as a poster surrogate. No autoplay; preload="metadata"
                pulls just enough bytes to decode the first frame.
            Decorative (`aria-hidden`) and silent (`muted`). `playsInline`
            is required for iOS Safari; `disablePictureInPicture` keeps
            it visually inert. */}
        {previewUrl && (
          <video
            ref={videoRef}
            src={previewUrl}
            muted
            autoPlay={hovered}
            loop={hovered}
            playsInline
            preload="metadata"
            disablePictureInPicture
            controls={false}
            aria-hidden
            tabIndex={-1}
            onPlaying={() => setPreviewPlaying(true)}
            onPause={() => setPreviewPlaying(false)}
            onError={() => { setPreviewUrl(null); setPreviewPlaying(false); previewRequestedRef.current = false; }}
            className={[
              'absolute inset-0 w-full h-full object-cover pointer-events-none',
              'transition-opacity duration-200 ease-out',
              // Visible whenever (a) hover-preview is playing, OR
              // (b) we're using it as a poster surrogate (no static
              // poster + not hovered). Either way the user sees real
              // video pixels, never a black tile.
              (previewPlaying || (!posterUrl && !hovered)) ? 'opacity-100' : 'opacity-0',
            ].join(' ')}
          />
        )}        <div className="absolute inset-0 flex items-center justify-center">
          {/* Play glyph */}
          <div
            className={[
              'flex items-center justify-center rounded-full',
              'w-14 h-14 backdrop-blur-md',
              'bg-black/40 border border-white/30',
              'transition-all duration-150',
              hovered ? 'scale-110 border-neon_orange-300/80' : 'scale-100',
            ].join(' ')}
          >
            <svg viewBox="0 0 24 24" className="w-6 h-6 ml-0.5 fill-white" aria-hidden>
              <path d="M8 5v14l11-7z" />
            </svg>
          </div>
        </div>

        {/* Top-left container chip — hidden in dense (IG) mode */}
        {!dense && (
          <div className="absolute top-2 left-2 px-2 py-0.5 rounded text-[10px] uppercase tracking-wide
                          bg-black/60 text-neutral-300 font-mono backdrop-blur-sm border border-white/10">
            {item.container || 'video'}
          </div>
        )}

        {/* Freshness chip — stacked below the container chip. Only renders
            when the row is not authoritative-live, so steady-state cards
            stay visually clean. Tones are PAC Glass-aligned dark-only. */}
        {!dense && item.freshness && item.freshness !== 'live' && (
          <div
            className={[
              'absolute top-9 left-2 px-2 py-0.5 rounded text-[10px] uppercase tracking-wide',
              'font-mono backdrop-blur-sm border',
              item.freshness === 'syncing'
                ? 'bg-amber-500/15 text-amber-200 border-amber-300/40'
                : item.freshness === 'stale'
                ? 'bg-neutral-700/50 text-neutral-300 border-neutral-400/30'
                : item.freshness === 'offline'
                ? 'bg-rose-600/15 text-rose-200 border-rose-300/40'
                : 'bg-black/60 text-neutral-300 border-white/10',
            ].join(' ')}
            title={
              item.freshness === 'syncing'
                ? 'Healed from GCS — orchestrator catching up'
                : `Data freshness: ${item.freshness}`
            }
          >
            {item.freshness}
          </div>
        )}

        {/* Sidecar lifecycle badge — only shown when the asset has
            pending repair work or permanent_failed kinds. Steady-state
            cards render nothing for visual quiet. See SidecarsBadge.tsx
            for the wire contract (digestVideo → sidecars_state digest). */}
        <div className="absolute bottom-2 left-2 z-10">
          <SidecarsBadge item={item} compact={dense} />
        </div>

        {/* Top-right status badge — hidden in dense (IG) mode for a clean tile */}
        {!dense && (
          <div className={[
            'absolute top-2 right-2 px-2 py-0.5 rounded text-[10px] font-semibold border backdrop-blur-sm',
            tone.text, tone.bg, tone.border,
            // Pulse during the cold-start window so "Warming up…" reads as a
            // live, transient state rather than a settled status chip.
            progress.isWarming ? 'animate-pulse' : '',
          ].join(' ')}>
            {tone.label}
          </div>
        )}

        {/* IG-style multi-asset stack glyph — only visible when dense and
            we have a preview sidecar (so users get a TikTok/IG-like cue
            that hovering / tapping reveals motion). */}
        {dense && requestPreview && (
          <div className="absolute top-1.5 right-1.5 opacity-90 pointer-events-none" aria-hidden>
            <svg viewBox="0 0 24 24" className="w-4 h-4 fill-white drop-shadow-[0_1px_2px_rgba(0,0,0,0.7)]">
              <path d="M19 3H8a2 2 0 00-2 2v1H5a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-1h1a2 2 0 002-2V5a2 2 0 00-2-2zm-3 16H5V8h11v11zm3-3h-1V8a2 2 0 00-2-2H8V5h11v11z" />
            </svg>
          </div>
        )}

        {/* Trash button — reveal on hover/focus, top-right under status badge */}
        {onDelete && (
          <button
            type="button"
            onClick={handleDelete}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') handleDelete(e);
            }}
            disabled={deleting}
            aria-label={`Delete ${item.display_name ?? `video ${item.scope_key}`}`}
            title="Delete video"
            className={[
              'absolute top-9 right-2 w-7 h-7 flex items-center justify-center rounded-md',
              'bg-black/60 border border-rose-400/30 text-rose-300/80 backdrop-blur-sm',
              'opacity-0 group-hover:opacity-100 focus-visible:opacity-100',
              'hover:bg-rose-500/30 hover:border-rose-300/70 hover:text-rose-100',
              'transition-all duration-150 disabled:opacity-50 disabled:cursor-not-allowed',
              'focus:outline-none focus-visible:ring-2 focus-visible:ring-rose-300/60',
            ].join(' ')}
          >
            <svg viewBox="0 0 24 24" className="w-3.5 h-3.5 fill-current" aria-hidden>
              <path d="M9 3h6l1 2h4v2H4V5h4l1-2zm-3 6h12l-1 12H7L6 9zm3 2v8h2v-8H9zm4 0v8h2v-8h-2z" />
            </svg>
          </button>
        )}

        {/* Per-video share — hover-revealed (same gating as the trash
            button: owner-side only). Mints a `/s/<token>` link pinned
            to this single video. Reuses the canonical share component
            for visual + behavioural parity with the Feed rail and the
            gallery-wide header button. */}
        {onDelete && (
          <div className="absolute top-16 right-2 opacity-0 group-hover:opacity-100 focus-within:opacity-100 transition-opacity duration-150 z-10">
            <ShareLinkButton videoId={item.video_id} variant="header" />
          </div>
        )}

        {/* Bottom-right duration */}
        {!dense && item.duration_ms > 0 && (
          <div className="absolute bottom-2 right-2 px-1.5 py-0.5 rounded text-[10px] font-mono
                          bg-black/70 text-neutral-200 border border-white/10">
            {formatDuration(item.duration_ms)}
          </div>
        )}

        {/* Top-right map pin (revealed on hover for tidy default state) */}
        {item.geo && (
          <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100
                          focus-within:opacity-100 transition-opacity duration-150 z-10">
            <MapPinLink geo={item.geo} videoLabel={item.display_name ?? item.scope_key} size="sm" />
          </div>
        )}

        {/* Bottom-left resolution */}
        {!dense && item.resolution && (
          <div className="absolute bottom-2 left-2 px-1.5 py-0.5 rounded text-[10px] font-mono
                          bg-black/70 text-neutral-200 border border-white/10">
            {item.resolution}
          </div>
        )}

        {/* Pipeline progress bar — YouTube/TikTok/IG idiom. Only renders
            while the video is mid-pipeline (not terminal); auto-hides once
            the row reaches sidecars_written or failed (the rose status
            chip already communicates the failed terminal). The bar sits at
            the very bottom of the poster so it doesn't crowd the play
            glyph and is visible at-a-glance across a grid of cards. */}
        {!dense && !progress.isTerminal && (
          <div
            className="absolute inset-x-0 bottom-0 h-0.5 bg-white/5"
            role="progressbar"
            aria-valuemin={0}
            aria-valuemax={100}
            aria-valuenow={progress.percent}
            aria-label={`${progress.label} — ${progress.percent}%`}
            title={`${progress.label} (${progress.percent}%)`}
          >
            <div
              className={[
                'h-full transition-[width] duration-500 ease-out',
                // Tone-coupled fill so the bar reads at-a-glance even on
                // tiny thumbnails — amber while initiated/uploading,
                // sky/blue/indigo/violet as it advances. Tracks `tone.text`
                // → corresponding bg utility (avoids dynamic class strings
                // so Tailwind's JIT can statically detect them).
                item.status === 'initiated'        ? 'bg-amber-400/80'
                : progress.isWarming               ? 'bg-amber-400/80'
                : item.status === 'uploaded'       ? 'bg-sky-400/80'
                : item.status === 'ingested'       ? 'bg-blue-400/80'
                : item.status === 'extracted'      ? 'bg-indigo-400/80'
                : item.status === 'transcribed'    ? 'bg-violet-400/80'
                : 'bg-white/30',
                // Indeterminate-style shimmer while initiated (we don't know
                // the byte-progress server-side without per-chunk pings, so
                // the bar pulses at ~16% to convey "actively working"
                // without lying about percent). Mirrors TikTok's
                // "Processing…" indeterminate state. The cold-start
                // "warming up" window pulses too — the worker is waking.
                item.status === 'initiated' || progress.isWarming ? 'animate-pulse' : '',
              ].join(' ')}
              style={{
                width: item.status === 'initiated'
                  ? '16%'
                  : `${Math.max(4, progress.percent)}%`,
              }}
            />
          </div>
        )}
      </div>

      {/* Meta body ───────────────────────────────────────────────── */}
      {!dense && (
      <div className="flex flex-col gap-1 p-3">
        {/*
          Title precedence (per c393b96b contract):
            • The BFF digester guarantees `display_name` is always a stable
              string for `upload:*` rows — real filename when known, else a
              deterministic synthetic `video-<runId8>`. We render it as the
              primary title in ALL cases, including error states.
            • `error` (when set) surfaces as a SECONDARY chip below the title
              with a label that matches the error code. This preserves a
              meaningful title while still flagging the problem and the
              actionable affordance.
        */}
        <h3
          className={[
            'text-sm font-semibold line-clamp-1',
            item.error ? 'text-rose-200' : 'text-neutral-100',
          ].join(' ')}
          title={item.display_name ?? undefined}
        >
          {item.display_name || 'Untitled'}
        </h3>
        {item.error && (
          <div
            className="text-[11px] text-rose-300/80 inline-flex items-center gap-1.5"
            title={`${item.error} for ${item.scope_key}`}
          >
            <svg viewBox="0 0 24 24" className="w-3 h-3 fill-current shrink-0" aria-hidden>
              <path d="M12 2L1 21h22L12 2zm0 6l7.53 13H4.47L12 8zm-1 4v4h2v-4h-2zm0 6v2h2v-2h-2z" />
            </svg>
            <span>
              {item.error === 'upload_orphan'
                ? `Orphan upload${
                    typeof item.error_detail?.age_minutes === 'number'
                      ? ` · ${item.error_detail.age_minutes}m old`
                      : ''
                  }`
                : 'Metadata unavailable'}
            </span>
          </div>
        )}
        {item.error && (
          <div
            className="text-[10px] text-rose-300/60 font-mono break-all line-clamp-1"
            title={item.scope_key}
          >
            {item.scope_key}
          </div>
        )}
        {item.error && item.error_detail?.attr_keys_present && (
          <div className="text-[10px] text-neutral-500 font-mono">
            attrs:{' '}
            {item.error_detail.attr_keys_present.length > 0
              ? item.error_detail.attr_keys_present.join(', ')
              : '∅ (none present)'}
          </div>
        )}

        <div className="flex items-center gap-2 text-[11px] text-neutral-400 flex-wrap">
          <span
            title={item.created_at ? `Captured: ${new Date(item.created_at).toLocaleString()}` : 'No capture date'}
            className="inline-flex items-center gap-1"
          >
            <svg viewBox="0 0 24 24" className="w-3 h-3 fill-current opacity-60" aria-hidden>
              <path d="M7 2h2v2h6V2h2v2h3v18H4V4h3V2zm-1 6v12h12V8H6z" />
            </svg>
            <LiveTimeAgo ms={item.created_at_ms} />
          </span>
          <span aria-hidden>•</span>
          <span
            title={item.uploaded_at ? `Uploaded: ${new Date(item.uploaded_at).toLocaleString()}` : 'No upload date'}
            className="inline-flex items-center gap-1"
          >
            <svg viewBox="0 0 24 24" className="w-3 h-3 fill-current opacity-60" aria-hidden>
              <path d="M5 20h14v-2H5v2zM12 2l-5.5 5.5 1.42 1.42L11 5.84V16h2V5.84l3.08 3.08 1.42-1.42L12 2z" />
            </svg>
            <LiveTimeAgo ms={item.uploaded_at_ms} />
          </span>
          <span aria-hidden>•</span>
          <span title="File size">{formatBytes(item.byte_length)}</span>
          {item.duration_ms > 0 && (
            <>
              <span aria-hidden>•</span>
              <span title="Duration">{formatDuration(item.duration_ms)}</span>
            </>
          )}
          {item.has_audio && (
            <>
              <span aria-hidden>•</span>
              <span className="text-emerald-400/80">audio</span>
            </>
          )}
        </div>

        {item.transcript_preview && (
          <p className="mt-1 text-[11px] text-neutral-500 line-clamp-2 italic">
            “{item.transcript_preview}”
          </p>
        )}
      </div>
      )}
    </article>
  );
}

export default memo(VideoCardImpl);
